import { useCallback, useEffect, useRef, useState } from "react";
import { Vector2 } from "three";
import * as classes from "./joystick.module.scss";

type Vec2 = {
  x: number;
  y: number;
};

const TMP_V2 = new Vector2();
(window as any).joystick = (window as any).joystick || {};

const Joystick = ({ name }: { name: string }) => {
  const rafRef = useRef<any>();
  const rootEl = useRef<HTMLDivElement | null>(null);
  const [radius, setRadius] = useState(0);
  const [touchStartPosition, setTouchStartPosition] = useState<Vec2 | null>(
    null
  );
  const [offset, setOffset] = useState<Vec2>({ x: 0, y: 0 });
  const [touchIndex, setTouchIndex] = useState<number | null>(null);

  useEffect(() => {
    (window as any).joystick[name] = { x: 0, y: 0 };

    const onResize = () => {
      setRadius(rootEl.current.clientWidth * 0.5);
    };
    window.addEventListener("resize", onResize);
    setRadius(rootEl.current.clientWidth * 0.5);

    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const value = {
      x: offset.x / radius,
      y: (offset.y / radius) * -1,
    };
    (window as any).joystick[name] = value;
  }, [offset, radius]);

  const onTouchStart = useCallback(
    (e) => {
      const touchIndex = e.touches ? e.touches.length - 1 : null;
      setTouchIndex(e.touches ? e.touches.length - 1 : null);

      const { clientX: x, clientY: y } = e.touches ? e.touches[touchIndex] : e;
      setTouchStartPosition({ x, y });
    },
    [radius]
  );

  const onTouchEnd = useCallback(
    (e) => {
      setTouchStartPosition(null);
      // setOffset({ x: 0, y: 0 });
    },
    [radius]
  );

  const onTouchMove = useCallback(
    (e) => {
      const { clientX: x, clientY: y } = e.touches ? e.touches[touchIndex] : e;

      TMP_V2.x = x - touchStartPosition.x;
      TMP_V2.y = y - touchStartPosition.y;
      TMP_V2.clampLength(0, radius);

      const offset = {
        x: TMP_V2.x,
        y: TMP_V2.y,
      };

      setOffset(offset);
    },
    [radius, touchStartPosition, touchIndex]
  );

  useEffect(() => {
    if (touchStartPosition) {
      cancelAnimationFrame(rafRef.current);
      window.addEventListener("mousemove", onTouchMove);
      window.addEventListener("touchmove", onTouchMove);
      window.addEventListener("touchend", onTouchEnd);
      window.addEventListener("mouseup", onTouchEnd);
    } else {
      const targetOffset = { x: 0, y: 0 };
      const currentOffset = { x: offset.x, y: offset.y };
      let then = Date.now();

      const animateToCenter = () => {
        const now = Date.now();
        const delta = Math.min((now - then) / 16.666, 4);
        currentOffset.x += (targetOffset.x - currentOffset.x) * 0.1 * delta;
        currentOffset.y += (targetOffset.y - currentOffset.y) * 0.1 * delta;

        TMP_V2.x = currentOffset.x;
        TMP_V2.y = currentOffset.y;
        TMP_V2.clampLength(0, radius);

        currentOffset.x = TMP_V2.x;
        currentOffset.y = TMP_V2.y;

        if (Math.abs(currentOffset.x) < 1 && Math.abs(currentOffset.y) < 1) {
          currentOffset.x = 0;
          currentOffset.y = 0;
        } else {
          then = now;
          rafRef.current = requestAnimationFrame(animateToCenter);
        }

        setOffset({ ...currentOffset });
      };

      rafRef.current = requestAnimationFrame(animateToCenter);
    }

    return () => {
      window.removeEventListener("mousemove", onTouchMove);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("mouseup", onTouchEnd);
    };
  }, [touchStartPosition]);

  return (
    <div className={classes.root} ref={rootEl}>
      <div
        onTouchStart={onTouchStart}
        onMouseDown={onTouchStart}
        className={classes.pad}
        style={{ transform: `translate(${offset.x}px, ${offset.y}px)` }}
      ></div>
    </div>
  );
};

export default Joystick;
